import { useContext, useEffect, useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, FormHelperText, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { UserDetailsContext } from '../../../context/user-details/user-details';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import UserAuthService from '../../../shared/services/user-auth/user-auth.service';
import { TermsAndConditionsButton } from '../../presentation-components/signup-login/terms-and-conditions-button';
import { AuthLayout } from './auth/auth-layout';
import { AuthField } from './auth/auth-field';
import { PasswordAdornment } from './auth/password-adornment';
import { useAuthBranding } from './auth/use-auth-branding';
import { AUTH_TOKENS, BRAND_DISPLAY_FONT } from './auth/auth-branding';

export const CompleteSignUp = () => {
  const navigate = useNavigate();
  const userAuthService = new UserAuthService();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const userDetailsContext = useContext(UserDetailsContext);
  const branding = useAuthBranding();

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const organisationDetails = Yup.object().shape({
    organisationName: Yup.string().required('Organisation name is required'),
    countryId: Yup.string().required('Please select the country where your organisation is headquartered.'),
    firstName: Yup.string().required('First name is required'),
    surname: Yup.string().required('Last name is required'),
    password: Yup.string().min(8, 'Your password must be at least 8 characters').required('Password is required'),
    passwordConfirmation: Yup.string().oneOf([Yup.ref('password')], 'Passwords must match').required('You must confirm your password'),
    termsAndConditions: Yup.boolean().oneOf([true], 'You must agree to the terms and conditions'),
    email: Yup.string().email('Email is invalid').required('Email is required'),
  });

  const formik = useFormik({
    initialValues: {
      firstName: '',
      surname: '',
      email: '',
      password: '',
      passwordConfirmation: '',
      organisationName: '',
      termsAndConditions: false,
      countryId: 'GBR',
    },
    validationSchema: organisationDetails,
    onSubmit: (values) => {
      setErrorMessage('');
      return userAuthService
        .completeEmailOnlyRegistration(values)
        .then(() => {
          userDetailsContext.forceUpdateUserDetails().then((updatedUserDetails) => {
            navigate(updatedUserDetails?.isPartner ? '/partner-dashboard' : '/dashboard', updatedUserDetails?.isPartner ? undefined : { state: { justAuthenticated: true } });
          });
        })
        .catch((err) => {
          console.log(err);
          if (err.response?.data?.message) {
            setErrorMessage(err.response.data.message);
          } else {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          }
        });
    },
  });

  // Carry the verified email through without rendering it as an editable field.
  useEffect(() => {
    if (userDetailsContext.userDetails?.email) {
      formik.setFieldValue('email', userDetailsContext.userDetails.email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetailsContext.userDetails]);

  return (
    <AuthLayout branding={branding}>
      <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ width: '100%' }}>
        <Typography
          component="h1"
          sx={{ mb: '6px', fontSize: '1.6rem', fontFamily: BRAND_DISPLAY_FONT, letterSpacing: '-0.015em', color: 'text.primary' }}
        >
          Create your account
        </Typography>
        <Typography sx={{ mb: '26px', fontSize: '0.9375rem', lineHeight: 1.5, color: AUTH_TOKENS.inkSecondary }}>
          Just a few details to finish setting up your {branding.productName} workspace.
        </Typography>

        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <AuthField
              id="firstName"
              name="firstName"
              label="First name"
              placeholder="First name"
              autoComplete="given-name"
              icon={<PersonOutlined />}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName ? formik.errors.firstName : undefined}
              focusRing={branding.focusRing}
            />
            <AuthField
              id="surname"
              name="surname"
              label="Last name"
              placeholder="Last name"
              autoComplete="family-name"
              icon={<PersonOutlined />}
              value={formik.values.surname}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.surname && Boolean(formik.errors.surname)}
              helperText={formik.touched.surname ? formik.errors.surname : undefined}
              focusRing={branding.focusRing}
            />
          </Stack>

          <AuthField
            id="organisationName"
            name="organisationName"
            label="Organisation or business name"
            placeholder="Acme Ltd"
            autoComplete="organization"
            icon={<BusinessOutlined />}
            value={formik.values.organisationName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.organisationName && Boolean(formik.errors.organisationName)}
            helperText={formik.touched.organisationName ? formik.errors.organisationName : undefined}
            focusRing={branding.focusRing}
          />

          <AuthField
            id="password"
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            icon={<LockOutlined />}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password ? formik.errors.password : undefined}
            focusRing={branding.focusRing}
            endAdornment={<PasswordAdornment visible={showPassword} onToggle={() => setShowPassword((v) => !v)} />}
          />

          <AuthField
            id="passwordConfirmation"
            name="passwordConfirmation"
            label="Confirm password"
            type={showConfirm ? 'text' : 'password'}
            placeholder="Re-enter your password"
            autoComplete="new-password"
            icon={<LockOutlined />}
            value={formik.values.passwordConfirmation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.passwordConfirmation && Boolean(formik.errors.passwordConfirmation)}
            helperText={formik.touched.passwordConfirmation ? formik.errors.passwordConfirmation : undefined}
            focusRing={branding.focusRing}
            endAdornment={<PasswordAdornment visible={showConfirm} onToggle={() => setShowConfirm((v) => !v)} />}
          />
        </Stack>

        <FormControlLabel
          sx={{ mt: '16px', alignItems: 'flex-start', ml: '-3px' }}
          control={
            <Checkbox
              id="termsAndConditions"
              name="termsAndConditions"
              checked={formik.values.termsAndConditions}
              onChange={formik.handleChange}
              disableRipple
              sx={{ py: 0, pr: 1 }}
            />
          }
          label={
            <Typography sx={{ fontSize: '0.875rem', color: AUTH_TOKENS.inkSecondary, lineHeight: 1.5 }}>
              I accept the
              <TermsAndConditionsButton color={branding.secrInk} />
            </Typography>
          }
        />
        {formik.touched.termsAndConditions && formik.errors.termsAndConditions && (
          <FormHelperText error sx={{ ml: '2px', fontSize: '0.8125rem' }}>
            {formik.errors.termsAndConditions}
          </FormHelperText>
        )}

        {errorMessage && (
          <FormHelperText error role="alert" sx={{ mt: 1.5, ml: '2px', fontSize: '0.8125rem' }}>
            {errorMessage}
          </FormHelperText>
        )}

        <Button
          type="submit"
          fullWidth
          size="large"
          disabled={formik.isSubmitting}
          sx={{ mt: '24px', height: 50, fontSize: '0.9375rem' }}
        >
          {formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Create account'}
        </Button>
      </Box>
    </AuthLayout>
  );
};
